/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { useRetracked } from 'js/lib/retracked';
import { useLocation } from 'js/lib/useRouter';

import { Button, Dialog, Typography2 } from '@coursera/cds-core';

import { useDegreeDescriptionPageTheme } from 'bundles/degree-description/degreeDescriptionPageTheme';
import {
  routerLocationHasEnrollApplyHash,
  routerLocationHasRequestInfoHash,
} from 'bundles/degree-description/utils/EoiUtils';
import type { PersonalizedMessage } from 'bundles/degree-description/utils/personalizedMessageContent';
import { getEoiButton } from 'bundles/degree-description/utils/personalizedMessageCriteria';
import { incrementClosedPersonalizedMessageCount } from 'bundles/degree-description/utils/personalizedMessageViews';
import EoiButton from 'bundles/expression-of-interest/components/EoiButton';
import type { EoiButtonsType } from 'bundles/expression-of-interest/types';
import { appendUtmParametersToApplicationLink } from 'bundles/expression-of-interest/utils/util';
import TrackedButton from 'bundles/page/components/TrackedButton';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

const { HeadingGroup, Content, Actions } = Dialog;

type Props = {
  slug?: string | null;
  personalizedMessage?: PersonalizedMessage | null;
  eoiButtons?: EoiButtonsType;
  ctaAction?: (() => void) | string | null;
  onXddp?: boolean;
};

const PersonalizedModal = ({ slug, personalizedMessage, eoiButtons, ctaAction, onXddp }: Props) => {
  const theme = useDegreeDescriptionPageTheme();
  const location = useLocation();

  const hasRequestInfo = routerLocationHasRequestInfoHash(location);
  const hasEnrollApply = routerLocationHasEnrollApplyHash(location);

  let defaultOpenState: boolean;

  if (onXddp) {
    // Don't open the modal if either of these hashes exist in the URL
    // Otherwise, the modal will cover over the EOI form on xddp
    defaultOpenState = !(hasRequestInfo || hasEnrollApply);
  } else {
    defaultOpenState = true;
  }

  const track = useRetracked();
  const [open, setOpen] = useState(defaultOpenState);

  if (!personalizedMessage || !slug) return null;

  const { title, body, ctaText, ctaActionType } = personalizedMessage;

  const messageData = personalizedMessage;
  const trackingName = ctaActionType === 'ENROLL_APPLY' ? 'apply' : 'email_me_info';

  const handleClose = () => {
    track({
      trackingName: 'personalized_message_modal_close',
      trackingData: messageData,
      action: 'click',
    });
    incrementClosedPersonalizedMessageCount(slug, personalizedMessage.id);
    setOpen(false);
  };

  const eoiButtonConfig = getEoiButton(ctaActionType, ctaText, 'primary', eoiButtons, handleClose);

  const ctaActionWithClose = () => {
    if (ctaAction && typeof ctaAction !== 'string') {
      ctaAction();
      handleClose();
    }
  };

  return (
    <div className="rc-PersonalizedModal">
      <Dialog open={open} variant="standard" onClose={handleClose} aria-describedby="personalized-modal">
        <TrackedDiv
          trackingName="personalized_message_modal"
          trackClicks={false}
          withVisibilityTracking
          data={messageData}
        >
          <HeadingGroup>{title}</HeadingGroup>
          <Content id="personalized-modal">
            <Typography2 component="p" variant="bodyPrimary" css={theme.mobileFontSizeStyle?.body}>
              {body}
            </Typography2>
          </Content>
          {eoiButtonConfig && (
            <Actions>
              <EoiButton actionConfig={eoiButtonConfig} trackingProductId="" />
            </Actions>
          )}
          {!eoiButtonConfig && ctaAction && (
            <Actions>
              {typeof ctaAction === 'string' ? (
                <Button
                  component={TrackedLink2}
                  data={messageData}
                  variant="primary"
                  href={appendUtmParametersToApplicationLink(ctaAction)}
                  target="_blank"
                  rel="noopener noreferrer"
                  trackingName={trackingName}
                >
                  {ctaText}
                </Button>
              ) : (
                <Button
                  component={TrackedButton}
                  data={messageData}
                  variant="primary"
                  onClick={ctaActionWithClose}
                  trackingName={trackingName}
                  withVisibilityTracking={false}
                  requireFullyVisible={false}
                >
                  {ctaText}
                </Button>
              )}
            </Actions>
          )}
        </TrackedDiv>
      </Dialog>
    </div>
  );
};

export default PersonalizedModal;
